import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { API_URL } from './config';
import ImpersonationBanner from './components/ImpersonationBanner';

// ============================================
// AUTH & LAYOUT
// ============================================
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import SetPassword from './pages/SetPassword';
import SubbieSignup from './pages/SubbieSignup';
import ClientSignup from './pages/ClientSignup';
import PublicSiteInspection from './pages/PublicSiteInspection';
import InspectorReport from './pages/InspectorReport';
import SiteInspectionDetail from './pages/SiteInspectionDetail';
import LiveMap from './pages/LiveMap';

// ============================================
// ADMIN PAGES
// ============================================
import AdminDashboard from './pages/AdminDashboard';
import AdminJobReview from './pages/AdminJobReview';
import AdminPayments from './pages/AdminPayments';
import AdminSubbieVerification from './pages/AdminSubbieVerification';
import AdminSubbieVerificationDetail from './pages/AdminSubbieVerificationDetail';
import AdminUploads from './pages/AdminUploads';
import AdminVerifyUpload from './pages/AdminVerifyUpload';
import AdminClientApproval from './pages/AdminClientApproval';
import AdminClientApprovalDetail from './pages/AdminClientApprovalDetail';
import ActiveJobs from './pages/ActiveJobs';
import NewJobs from './pages/NewJobs';
import CompletedJobs from './pages/CompletedJobs';
import CompletedJobDetail from './pages/CompletedJobDetail';
import ClientsPage from './pages/ClientsPage';
import ClientDetailPage from './pages/ClientDetailPage';
import SubbiesPage from './pages/SubbiesPage';
import SubcontractorDetailPage from './pages/SubcontractorDetailPage';
import LoadsPage from './pages/LoadsPage';
import AdminFleetOverview from './pages/AdminFleetOverview';
import AdminScheduler from './pages/AdminScheduler';
import InvoicesPage from './pages/InvoicesPage';
import ReportsPage from './pages/ReportsPage';
import SuppliersPage from './pages/SuppliersPage';
import AccountsOverviewPage from './pages/AccountsOverviewPage';
import AccountingHelpPage from './pages/AccountingHelpPage';
import HelpPage from './pages/HelpPage';
import ProjectManagementPage from './pages/ProjectManagementPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import PortalProjects from './pages/PortalProjects';
import SettingsPage from './pages/SettingsPage';
import UsersPage from './pages/UsersPage';
import ProfilePage from './pages/ProfilePage';
import AuditLogPage from './pages/AuditLogPage';
import SystemManagement from './pages/SystemManagement';
import NotificationsPage from './pages/NotificationsPage';
import MessagesDashboard from './pages/MessagesDashboard';
import EmailCenter from './pages/EmailCenter';
import CreateTender from './pages/CreateTender';
import TenderDashboard from './pages/TenderDashboard';
import TenderDetail from './pages/TenderDetail';

// ============================================
// QUOTES PAGES
// ============================================
import QuotesPage from './pages/QuotesPage';
import CreateQuote from './pages/CreateQuote';
import QuoteDetailPage from './pages/QuoteDetailPage';
import ContainerQuotePage from './pages/ContainerQuotePage';
import QuoteRegisterPage from './pages/QuoteRegisterPage';
import QuoteRequestDetailPage from './pages/QuoteRequestDetailPage';

// ============================================
// CRM PAGE
// ============================================
import CRMpage from './pages/CRMpage';
import CRMRecordDetail from './pages/CRMRecordDetail';

// ============================================
// CLIENT PAGES
// ============================================
import ClientDashboard from './pages/ClientDashboard';
import ClientNewJob from './pages/ClientNewJob';
import ClientJobs from './pages/ClientJobs';
import ClientTeam from './pages/ClientTeam';
import ClientJobDetail from './pages/ClientJobDetail';
import ClientLoadDetail from './pages/ClientLoadDetail';
import ClientCompletedJobs from './pages/ClientCompletedJobs';
import ClientInvoices from './pages/ClientInvoices';
import ClientLoads from './pages/ClientLoads';
import ClientReports from './pages/ClientReports';
import ClientProfile from './pages/ClientProfile';
import ClientSettings from './pages/ClientSettings';
import ClientHelp from './pages/ClientHelp';
import ClientFeedback from './pages/ClientFeedback';
import ClientQuoteApproval from './pages/ClientQuoteApproval';
import ClientQuotesPage from './pages/ClientQuotesPage';
import ClientQuoteDetail from './pages/ClientQuoteDetail';
import ClientPods from './pages/ClientPods';
import ClientMessages from './pages/ClientMessages';
import ClientNotifications from './pages/ClientNotifications';
import PodApprovalsPage from './pages/PodApprovalsPage';
import ClientContainerEstimatorPage from './pages/ClientContainerEstimatorPage';
import ClientLiveMap from './pages/ClientLiveMap';

// ============================================
// SUBBIE PAGES
// ============================================
import SubbieDashboard from './pages/SubbieDashboard';
import SubbieNewJobs from './pages/SubbieNewJobs';
import SubbieActiveJobs from './pages/SubbieActiveJobs';
import SubbieClosedJobs from './pages/SubbieClosedJobs';
import SubbieMessages from './pages/SubbieMessages';
import SubbieNotifications from './pages/SubbieNotifications';
import SubbieFleet from './pages/SubbieFleet';
import SubbieUploads from './pages/SubbieUploads';
import SubbieInvoices from './pages/SubbieInvoices';
import SubbieHours from './pages/SubbieHours';
import SubbieReports from './pages/SubbieReports';
import SubbieProfile from './pages/SubbieProfile';
import SubbieHistory from './pages/SubbieHistory';
import SubbieScheduler from './pages/SubbieScheduler';
import SubbieLoads from './pages/SubbieLoads';
import SubbieTenders from './pages/SubbieTenders';
import SubbieBidForm from './pages/SubbieBidForm';
import SubbieTenderView from './pages/SubbieTenderView';
import SubbieLiveMap from './pages/SubbieLiveMap';

// ============================================
// AUTH GUARD - PROTECTED ROUTES
// ============================================
// PHASE 5 — a "projects only" login may only open the project area
// (+ live map, loads, their own profile). The API refuses the rest too.
const PORTAL_ONLY_ALLOWED = [
  '/client-projects', '/client-live-map', '/client-loads', '/client-profile',
  '/client-help', '/client-feedback', '/client-settings',
  '/subbie-projects', '/subbie-live-map', '/subbie-loads', '/subbie-active-jobs',
  '/subbie-closed-jobs', '/subbie-fleet', '/subbie-uploads', '/subbie-profile',
  '/subbie-help', '/subbie-settings'
];
const portalPathAllowed = (pathname) => PORTAL_ONLY_ALLOWED.some((p) => pathname === p || pathname.startsWith(`${p}/`));

const ProtectedRoute = ({ children, allowedRoles }) => {
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/login" replace />;
  }

  // Projects-only logins are sent back to their project list.
  if (user.portal_scope === 'projects_only' && !portalPathAllowed(window.location.pathname)) {
    return <Navigate to={user.role === 'client' ? '/client-projects' : '/subbie-projects'} replace />;
  }

  return children;
};

// ============================================
// MAIN APP
// ============================================
function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // A magic-link invite puts an access_token in the URL hash. If the user is
    // already logged in (e.g. an admin creating a new staff account while still
    // logged in as themselves), the old session MUST be cleared first — otherwise
    // the app bounces straight to the previous user's dashboard and the invite
    // never reaches the "set your password" screen.
    const hasMagicLink = window.location.hash.includes('access_token');
    if (hasMagicLink) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('subbieId');
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');

    if (token && user.role) {
      setIsAuthenticated(true);
      setUserRole(user.role);
    }

    // Keep localStorage user in sync with the DB (single source of truth).
    // Fixes stale avatar_url / profile data after it changed elsewhere.
    if (token) {
      fetch(`${API_URL}/users/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
        .then(r => r.json())
        .then(data => {
          const fresh = data.user || data;
          if (fresh && fresh.id) {
            localStorage.setItem('user', JSON.stringify(fresh));
          }
        })
        .catch(() => { /* keep existing localStorage on network failure */ });
    }

    setLoading(false);
  }, []);

  const handleLogin = (role) => {
    setIsAuthenticated(true);
    setUserRole(role);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('subbieId');
    setIsAuthenticated(false);
    setUserRole(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="text-4xl mb-4 animate-spin">🔄</div>
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* While a super admin is viewing the portal as somebody else, this bar is on every screen. */}
      <ImpersonationBanner />

      <Routes>
        {/* ============================================
            PUBLIC ROUTES
        ============================================ */}
        <Route path="/login" element={
          isAuthenticated ? (
            userRole === 'client' ? <Navigate to="/client-dashboard" replace /> :
            userRole === 'subcontractor' || userRole === 'subbie' ? <Navigate to="/subbie-dashboard" replace /> :
            <Navigate to="/dashboard" replace />
          ) : (
            <Login onLogin={handleLogin} />
          )
        } />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/subbie-signup" element={<SubbieSignup />} />
        <Route path="/client-signup" element={<ClientSignup />} />
        <Route path="/inspection/:token" element={<PublicSiteInspection />} />
        <Route path="/inspection/:token/report" element={<InspectorReport />} />
        <Route path="/" element={<Navigate to="/login" replace />} />

        {/* ============================================
            ADMIN ROUTES (all allow 'user' for read-only)
        ============================================ */}
        <Route path="/dashboard" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <AdminDashboard onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/admin/client-approval" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <AdminClientApproval onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/admin/client-approval/:id" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <AdminClientApprovalDetail onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/admin/job-review" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <AdminJobReview onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/admin/uploads" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <AdminUploads onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/admin/verify-upload/:uploadId" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <AdminVerifyUpload onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/admin/subbie-verification" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <AdminSubbieVerification onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/admin/subbie-verification/:id" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <AdminSubbieVerificationDetail onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/active-jobs" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <ActiveJobs onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/new-jobs" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <NewJobs onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/completed-jobs" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <CompletedJobs onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/completed-jobs/:id" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <CompletedJobDetail onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/clients" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <ClientsPage onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/clients/:id" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <ClientDetailPage onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/subcontractors" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <SubbiesPage onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/subcontractors/:id" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <SubcontractorDetailPage onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/loads" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <LoadsPage onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/fleet-overview" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <AdminFleetOverview onLogout={handleLogout} />
          </ProtectedRoute>
        } />

        <Route path="/admin-scheduler" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <AdminScheduler onLogout={handleLogout} />
          </ProtectedRoute>
        } />

        {/* The SAME scheduler the subbie uses, opened in admin mode for a chosen subbie. */}
        <Route path="/admin-scheduler/schedule/:jobId" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <SubbieScheduler onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/admin/pod-approvals" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <PodApprovalsPage onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/live-map" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <LiveMap onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/admin-payments" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <AdminPayments onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/invoices" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <InvoicesPage onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/reports" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <ReportsPage onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/suppliers" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <SuppliersPage onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/accounts-overview" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <AccountsOverviewPage onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/project-management" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user', 'client', 'subbie']}>
            <ProjectManagementPage onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/project-management/:id" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user', 'client', 'subbie']}>
            <ProjectDetailPage onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/help" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <HelpPage onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/help/accounting" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <AccountingHelpPage onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/accounting-guide" element={<Navigate to="/help/accounting" replace />} />
        <Route path="/settings" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <SettingsPage onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/users" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <UsersPage onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <ProfilePage onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/audit-log" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <AuditLogPage onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/system-management" element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <SystemManagement onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/notifications" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <NotificationsPage onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/messages" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <MessagesDashboard onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/email-center" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <EmailCenter onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/create-tender" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <CreateTender onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/tender-dashboard" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <TenderDashboard onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/tender-dashboard/:id" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <TenderDetail onLogout={handleLogout} />
          </ProtectedRoute>
        } />

        {/* ✅ CRM ROUTES */}
        <Route path="/crm/records/:id" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <CRMRecordDetail onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/crm/:tab" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <CRMpage onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/crm" element={<Navigate to="/crm/onboarding" replace />} />

        {/* ✅ QUOTES ROUTES */}
        <Route path="/quotes" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <QuotesPage onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/quotes/create/container" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <ContainerQuotePage onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/quotes/create" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <CreateQuote onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/quotes/edit/:id/container" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <ContainerQuotePage onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/quotes/edit/:id" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <CreateQuote onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/quotes/register" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <QuoteRegisterPage onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/quotes/requests/:id" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <QuoteRequestDetailPage onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/quotes/:id" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <QuoteDetailPage onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/site-inspection/:token" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'user']}>
            <SiteInspectionDetail onLogout={handleLogout} />
          </ProtectedRoute>
        } />

        {/* ============================================
            CLIENT ROUTES
        ============================================ */}
        <Route path="/client-dashboard" element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientDashboard onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/client-projects" element={
          <ProtectedRoute allowedRoles={['client']}>
            <PortalProjects role="client" onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/client-projects/:id" element={
          <ProtectedRoute allowedRoles={['client']}>
            <PortalProjects role="client" onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/client-new-job" element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientNewJob onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/client-estimator" element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientContainerEstimatorPage onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/client-live-map" element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientLiveMap onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/client-jobs" element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientJobs onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/client-completed" element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientCompletedJobs onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/client-invoices" element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientInvoices onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/client-loads" element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientLoads onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/client-reports" element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientReports onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/client-profile" element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientProfile onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/client-team" element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientTeam onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/client-job-details/:id" element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientJobDetail onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/client-load-details/:id" element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientLoadDetail onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/client-settings" element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientSettings onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/client-help" element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientHelp onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/client-feedback" element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientFeedback onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/client-quote-approval" element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientQuoteApproval onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/client-quotes" element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientQuotesPage onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/client-quotes/:id" element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientQuoteDetail onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/client-pods" element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientPods onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/client-messages" element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientMessages onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/client-notifications" element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientNotifications onLogout={handleLogout} />
          </ProtectedRoute>
        } />

        {/* ============================================
            SUBBIE ROUTES
        ============================================ */}
        <Route path="/subbie-dashboard" element={
          <ProtectedRoute allowedRoles={['subcontractor', 'subbie']}>
            <SubbieDashboard onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/subbie-projects" element={
          <ProtectedRoute allowedRoles={['subcontractor', 'subbie']}>
            <PortalProjects role="subbie" onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/subbie-projects/:id" element={
          <ProtectedRoute allowedRoles={['subcontractor', 'subbie']}>
            <PortalProjects role="subbie" onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/subbie-new-jobs" element={
          <ProtectedRoute allowedRoles={['subcontractor', 'subbie']}>
            <SubbieNewJobs onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/subbie-active-jobs" element={
          <ProtectedRoute allowedRoles={['subcontractor', 'subbie']}>
            <SubbieActiveJobs onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/subbie-closed-jobs" element={
          <ProtectedRoute allowedRoles={['subcontractor', 'subbie']}>
            <SubbieClosedJobs onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/subbie-messages" element={
          <ProtectedRoute allowedRoles={['subcontractor', 'subbie']}>
            <SubbieMessages onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/subbie-notifications" element={
          <ProtectedRoute allowedRoles={['subcontractor', 'subbie']}>
            <SubbieNotifications onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/subbie-fleet" element={
          <ProtectedRoute allowedRoles={['subcontractor', 'subbie']}>
            <SubbieFleet onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/subbie-uploads" element={
          <ProtectedRoute allowedRoles={['subcontractor', 'subbie']}>
            <SubbieUploads onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/subbie-payments" element={
          <ProtectedRoute allowedRoles={['subcontractor', 'subbie']}>
            <SubbieInvoices onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/subbie-hours" element={
          <ProtectedRoute allowedRoles={['subcontractor', 'subbie']}>
            <SubbieHours onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/subbie-reports" element={
          <ProtectedRoute allowedRoles={['subcontractor', 'subbie']}>
            <SubbieReports onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/subbie-profile" element={
          <ProtectedRoute allowedRoles={['subcontractor', 'subbie']}>
            <SubbieProfile onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/subbie-history" element={
          <ProtectedRoute allowedRoles={['subcontractor', 'subbie']}>
            <SubbieHistory onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/subbie-scheduler/:jobId" element={
          <ProtectedRoute allowedRoles={['subcontractor', 'subbie']}>
            <SubbieScheduler onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/subbie-loads" element={
          <ProtectedRoute allowedRoles={['subcontractor', 'subbie']}>
            <SubbieLoads onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/subbie-invoices" element={
          <ProtectedRoute allowedRoles={['subcontractor', 'subbie']}>
            <SubbieInvoices onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/subbie-tenders" element={
          <ProtectedRoute allowedRoles={['subcontractor', 'subbie']}>
            <SubbieTenders onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/subbie-tenders/:id/bid" element={
          <ProtectedRoute allowedRoles={['subcontractor', 'subbie']}>
            <SubbieBidForm onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/subbie-tenders/:id" element={
          <ProtectedRoute allowedRoles={['subcontractor', 'subbie']}>
            <SubbieTenderView onLogout={handleLogout} />
          </ProtectedRoute>
        } />
        <Route path="/subbie-live-map" element={
          <ProtectedRoute allowedRoles={['subcontractor', 'subbie']}>
            <SubbieLiveMap onLogout={handleLogout} />
          </ProtectedRoute>
        } />

        {/* ============================================
            FALLBACK
        ============================================ */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Router>
  );
}

export default App;